import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

type UseElementWidthResult = {
	width: number;
	/** Callback ref to attach to the element being measured. */
	setNode: (node: HTMLDivElement | null) => void;
	/** Read-only ref to the most recently attached node, for non-render reads. */
	nodeRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Tracks the rendered width of a DOM element via ResizeObserver.
 * Pass `setNode` as the element's callback ref; `width` stays in sync.
 * Bails out of setState when the value hasn't changed to avoid wasted renders.
 */
export const useElementWidth = (): UseElementWidthResult => {
	const [width, setWidth] = useState(0);
	const nodeRef = useRef<HTMLDivElement | null>(null);
	const lastWidthRef = useRef(0);

	const commitWidth = useCallback((nextWidth: number) => {
		if (nextWidth <= 0 || nextWidth === lastWidthRef.current) {
			return;
		}
		lastWidthRef.current = nextWidth;
		setWidth(nextWidth);
	}, []);

	const setNode = useCallback(
		(node: HTMLDivElement | null) => {
			nodeRef.current = node;
			if (node) {
				commitWidth(node.getBoundingClientRect().width);
			}
		},
		[commitWidth],
	);

	useEffect(() => {
		const el = nodeRef.current;
		if (!el) {
			return;
		}
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				commitWidth(entry.contentRect.width);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [commitWidth]);

	return { width, setNode, nodeRef };
};

type UseScrollStateArgs = {
	/** Starting point for resolving the page's actual scroll container. */
	containerRef: React.RefObject<HTMLDivElement | null>;
	/** Filters row ref — its height drives the insufficient-overflow safety margin. */
	filtersRowRef: React.RefObject<HTMLDivElement | null>;
};

type UseScrollStateResult = {
	/** True when the resolved scroll source is scrolled past the top. */
	isScrolled: boolean;
	/**
	 * True when the scroll source has too little overflow for auto-collapse to
	 * be safe. Collapsing would remove enough height to clamp scrollTop back to
	 * 0, immediately re-expanding — causing visible oscillation. When true,
	 * scroll-driven expansion changes and the keyline are suppressed.
	 */
	hasInsufficientScrollOverflow: boolean;
};

const isScrollableElement = (el: HTMLElement): boolean => {
	const style = window.getComputedStyle(el);
	const overflowY = style.overflowY;
	return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
};

/**
 * Walks up from `start` looking for a scrollable ancestor; if none found,
 * checks siblings (covers the lite layout where the header and body are
 * siblings under a non-scrolling wrapper). Falls back to `window`.
 * Re-resolved on every scroll event so it picks up layout changes (e.g.
 * results loading after an initial empty-state render).
 */
const findScrollSource = (start: HTMLElement | null): HTMLElement | Window => {
	let node: HTMLElement | null = start?.parentElement ?? null;
	while (node && node !== document.body && node !== document.documentElement) {
		if (isScrollableElement(node)) {
			return node;
		}
		const siblings = Array.from(node.children) as HTMLElement[];
		for (const sibling of siblings) {
			if (sibling !== start && isScrollableElement(sibling)) {
				return sibling;
			}
		}
		node = node.parentElement;
	}
	return window;
};

const computeIsScrolled = (source: HTMLElement | Window): boolean => {
	if (source === window) {
		return window.scrollY > 0;
	}
	return (source as HTMLElement).scrollTop > 0;
};

const getScrollOverflow = (source: HTMLElement | Window): number => {
	if (source === window) {
		return Math.max(
			0,
			document.documentElement.scrollHeight - document.documentElement.clientHeight,
		);
	}
	const el = source as HTMLElement;
	return Math.max(0, el.scrollHeight - el.clientHeight);
};

// Safety margin for the insufficient-scroll-overflow guard.
// Overflow must exceed (filterRowHeight + buffer) to allow auto-collapse.
const SAFETY_MARGIN_FLOOR = 80;
const SAFETY_MARGIN_BUFFER = 24;

/**
 * Tracks scroll-derived state: `isScrolled` (drives the keyline border) and
 * `hasInsufficientScrollOverflow` (suppresses auto-collapse oscillation on
 * landing / no-results pages).
 *
 * The scroll source is re-resolved on every scroll event so that layout
 * changes (e.g. results loading after an empty-state render) are picked up
 * immediately without any caching complexity.
 */
export const useScrollState = ({
	containerRef,
	filtersRowRef,
}: UseScrollStateArgs): UseScrollStateResult => {
	const [isScrolled, setIsScrolled] = useState(false);
	const [hasInsufficientScrollOverflow, setHasInsufficientScrollOverflow] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		const handleScroll = () => {
			// Re-resolve the scroll source on every scroll so layout changes
			// (e.g. a body container becoming scrollable after results load)
			// are always reflected without stale-cache issues.
			const source = findScrollSource(containerRef.current);

			setIsScrolled(computeIsScrolled(source));

			const filterRowHeight = filtersRowRef.current?.offsetHeight ?? 0;
			const safetyMargin = Math.max(filterRowHeight, SAFETY_MARGIN_FLOOR) + SAFETY_MARGIN_BUFFER;
			setHasInsufficientScrollOverflow(getScrollOverflow(source) < safetyMargin);
		};

		// Initialise synchronously so deep-linked pages render the correct state on first paint.
		handleScroll();

		// `scroll` doesn't bubble — use capture on document to catch all sources.
		// Also bind window directly for document-level scrolling layouts.
		const unbindDocument = bind(document, {
			type: 'scroll',
			listener: handleScroll,
			options: { capture: true, passive: true },
		});
		const unbindWindow = bind(window, {
			type: 'scroll',
			listener: handleScroll,
			options: { passive: true },
		});
		return () => {
			unbindDocument();
			unbindWindow();
		};
	}, [containerRef, filtersRowRef]);

	return { isScrolled, hasInsufficientScrollOverflow };
};

type UseFiltersExpansionSyncArgs = {
	isScrolled: boolean;
	hasInsufficientScrollOverflow: boolean;
	hasSelectedFilters: boolean;
	setIsFiltersExpanded: (next: boolean) => void;
};

/**
 * Drives the filter row's expansion state from scroll signals.
 * Precedence: selected filters → always expanded; insufficient overflow →
 * leave unchanged (avoids oscillation); otherwise follow scroll position.
 */
export const useFiltersExpansionSync = ({
	isScrolled,
	hasInsufficientScrollOverflow,
	hasSelectedFilters,
	setIsFiltersExpanded,
}: UseFiltersExpansionSyncArgs): void => {
	useEffect(() => {
		if (hasSelectedFilters) {
			setIsFiltersExpanded(true);
			return;
		}
		if (hasInsufficientScrollOverflow) {
			return;
		}
		setIsFiltersExpanded(!isScrolled);
	}, [isScrolled, hasSelectedFilters, hasInsufficientScrollOverflow, setIsFiltersExpanded]);
};

type UseToggleClusterPositionArgs = {
	appFilterBarRowEl: HTMLDivElement | null;
	filtersRowEl: HTMLDivElement | null;
	isFiltersExpanded: boolean;
	/** Re-triggers position computation on container resize. */
	containerWidth: number;
};

type UseToggleClusterPositionResult = {
	/** Inline `top` (px) for the cluster; interpreted as its CENTER Y via `translateY(-50%)`. */
	toggleClusterTop: number;
	/** Measured cluster width (px) — drives the row spacers. */
	toggleClusterWidth: number;
	/**
	 * False until both `top` and `width` are first measured. While false the
	 * cluster is hidden and transitions are suppressed to prevent a first-paint
	 * flash from y=0 to the measured anchor.
	 */
	hasMeasuredInitialPosition: boolean;
	/**
	 * False until at least one paint has occurred with the cluster visible at
	 * its measured position. Used to keep `transition: none` in place for the
	 * "reveal" paint so the browser doesn't interpolate from the initial
	 * `top: 0` to the measured `top` when the visibility / transition styles
	 * are flipped on. Becomes true on the next animation frame after the
	 * initial measurement commits, enabling transitions for subsequent
	 * expand / collapse toggles.
	 */
	areTransitionsEnabled: boolean;
	/** Callback ref to attach to the cluster element for width measurement. */
	setToggleClusterEl: (node: HTMLDivElement | null) => void;
};

// Aligns the cluster center with filter pill centers (pills have outer padding
// that shifts their visual center slightly below a plain button's center).
const PILL_INNER_PADDING_OFFSET = 3;
// Vertical gap between the appFilterBar row and the filters row.
const ROW_GAP = 12;

/**
 * Measures the toggle cluster's `top` position between its collapsed
 * (appFilterBar row) and expanded (filters row) anchors, and tracks whether
 * the initial measurement has committed (for the first-paint visibility guard).
 *
 * Uses `useLayoutEffect` so measurements are flushed before the browser paints,
 * preventing the cluster from briefly appearing at y=0 on initial load.
 */
export const useToggleClusterPosition = ({
	appFilterBarRowEl,
	filtersRowEl,
	isFiltersExpanded,
	containerWidth,
}: UseToggleClusterPositionArgs): UseToggleClusterPositionResult => {
	const [toggleClusterTop, setToggleClusterTop] = useState(0);
	const { width: toggleClusterWidth, setNode: setToggleClusterEl } = useElementWidth();
	const [hasMeasuredInitialPosition, setHasMeasuredInitialPosition] = useState(false);
	const [areTransitionsEnabled, setAreTransitionsEnabled] = useState(false);

	useLayoutEffect(() => {
		if (!appFilterBarRowEl || !filtersRowEl) {
			return;
		}

		const computeTop = (): number => {
			const appFilterBarCenter = appFilterBarRowEl.offsetTop + appFilterBarRowEl.offsetHeight / 2;
			if (isFiltersExpanded) {
				return (
					appFilterBarCenter + appFilterBarRowEl.offsetHeight + ROW_GAP + PILL_INNER_PADDING_OFFSET
				);
			}
			return appFilterBarCenter;
		};

		// Bail out if the computed value hasn't changed (avoids renders on height-only resizes).
		let lastTop = -1;
		const commitTop = () => {
			const next = computeTop();
			if (next !== lastTop) {
				lastTop = next;
				setToggleClusterTop(next);
			}
		};
		commitTop();

		const observer = new ResizeObserver(commitTop);
		observer.observe(appFilterBarRowEl);
		observer.observe(filtersRowEl);
		return () => observer.disconnect();
	}, [isFiltersExpanded, containerWidth, appFilterBarRowEl, filtersRowEl]);

	// Reveal the cluster once both top and width are measured.
	// useLayoutEffect ensures the reveal is flushed in the same paint as the
	// first measurement so the cluster is never visible at y=0.
	useLayoutEffect(() => {
		if (hasMeasuredInitialPosition || toggleClusterTop === 0 || toggleClusterWidth === 0) {
			return;
		}
		setHasMeasuredInitialPosition(true);
	}, [hasMeasuredInitialPosition, toggleClusterTop, toggleClusterWidth]);

	useEffect(() => {
		if (!hasMeasuredInitialPosition || areTransitionsEnabled) {
			return;
		}
		let rafId2 = 0;
		const rafId1 = requestAnimationFrame(() => {
			rafId2 = requestAnimationFrame(() => {
				setAreTransitionsEnabled(true);
			});
		});
		return () => {
			cancelAnimationFrame(rafId1);
			if (rafId2) {
				cancelAnimationFrame(rafId2);
			}
		};
	}, [hasMeasuredInitialPosition, areTransitionsEnabled]);

	return {
		toggleClusterTop,
		toggleClusterWidth,
		hasMeasuredInitialPosition,
		areTransitionsEnabled,
		setToggleClusterEl,
	};
};
